// Languages: English (default) and Arabic (right-to-left).
//
//   const { t, pick, lang, isAr } = useI18n();
//   t('Add to cart')                    → "أضف إلى السلة" in Arabic, the English text otherwise
//   t('{n} items', { n: 3 })            → placeholders are filled in both languages
//   pick(product, 'name')               → product.nameAr in Arabic when filled in, else product.name
//
// English text is the key, so anything without a translation simply shows in English.
// Arabic translations live in ./locales/ar/*.js and ./locales/admin/*.js (each exports a plain
// { "English": "عربي" } object).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Plain localStorage here (not lib/format) because format.js imports this file.
const saved = () => { try { return localStorage.getItem('tev_lang'); } catch { return null; } };
const save = (v) => { try { localStorage.setItem('tev_lang', v); } catch { /* storage unavailable */ } };

// Arabic texts are downloaded only when Arabic is used (English visitors never fetch them).
// The admin panel adds its own dictionaries (./locales/admin/) with addTranslations().
const files = import.meta.glob('./locales/ar/*.js', { import: 'default' });
const AR = {};
let arabicLoading = null;
export function loadArabic() {
  arabicLoading ||= Promise.all(Object.values(files).map((load) => load())).then((dicts) => { Object.assign(AR, ...dicts); });
  return arabicLoading;
}
export function addTranslations(...dicts) { Object.assign(AR, ...dicts); }

const LANGS = { en: { label: 'English', short: 'EN', dir: 'ltr' }, ar: { label: 'العربية', short: 'عربي', dir: 'rtl' } };

// A link with ?lang=ar or ?lang=en opens the site in that language (and remembers it).
const fromUrl = (() => { try { return new URLSearchParams(window.location.search).get('lang'); } catch { return null; } })();
if (fromUrl === 'ar' || fromUrl === 'en') save(fromUrl);
let current = (fromUrl || saved()) === 'ar' ? 'ar' : 'en';
export const currentLang = () => current;

const fill = (s, vars) => (vars ? String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s);

// Usable outside React (e.g. the API helper translating server messages).
export function translate(text, vars) {
  if (text == null) return text;
  return fill(current === 'ar' ? (AR[text] ?? text) : text, vars);
}

export function pickField(obj, field) {
  if (!obj) return '';
  const ar = obj[`${field}Ar`];
  return current === 'ar' && typeof ar === 'string' && ar.trim() ? ar : obj[field];
}

function applyToDocument(lang) {
  const el = document.documentElement;
  el.lang = lang;
  el.dir = LANGS[lang].dir;
  // The Arabic web font is only downloaded once Arabic is used (see index.html).
  if (lang === 'ar') window.__loadFont?.('IBM+Plex+Sans+Arabic:wght@400;500;600;700', 'font-arabic');
}
applyToDocument(current);

const I18nCtx = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(current);
  const setLang = useCallback(async (l) => {
    const next = l === 'ar' ? 'ar' : 'en';
    if (next === 'ar') await loadArabic().catch(() => {});
    current = next;
    save(next);
    applyToDocument(next);
    setLangState(next);
  }, []);
  useEffect(() => { applyToDocument(lang); }, [lang]);

  const value = useMemo(() => ({
    lang,
    isAr: lang === 'ar',
    dir: LANGS[lang].dir,
    setLang,
    t: (text, vars) => fill(lang === 'ar' ? (AR[text] ?? text) : text, vars),
    pick: (obj, field) => {
      if (!obj) return '';
      const ar = obj[`${field}Ar`];
      return lang === 'ar' && typeof ar === 'string' && ar.trim() ? ar : obj[field];
    },
  }), [lang, setLang]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);

// EN / عربي toggle. `dark` for use on the navy admin sidebar; `compact` is a single
// round button showing the other language (for narrow phone headers).
export function LanguageSwitch({ dark = false, compact = false, className = '' }) {
  const { lang, setLang, t } = useI18n();
  const base = dark ? 'border-white/20 text-slate-300' : 'border-slate-200 bg-white text-slate-600';
  if (compact) {
    const other = lang === 'ar' ? 'en' : 'ar';
    return (
      <button type="button" onClick={() => setLang(other)} lang={other} aria-label={t('Language')} title={LANGS[other].label}
        className={`grid size-10 shrink-0 cursor-pointer place-items-center rounded-full border text-[13px] font-bold transition hover:border-brand-500 hover:text-brand-600 ${base} ${className}`}>
        {LANGS[other].short}
      </button>
    );
  }
  return (
    <div className={`inline-flex h-9 shrink-0 overflow-hidden rounded-full border text-[13px] font-bold ${base} ${className}`} role="group" aria-label={t('Language')}>
      {Object.entries(LANGS).map(([k, v]) => (
        <button key={k} type="button" onClick={() => setLang(k)} aria-pressed={lang === k} lang={k}
          className={`cursor-pointer px-3 transition ${lang === k ? 'bg-brand-500 text-white' : dark ? 'hover:text-white' : 'hover:text-brand-600'}`}>
          {v.short}
        </button>
      ))}
    </div>
  );
}
